import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { animate, group, query, style, transition, trigger, state, animateChild } from '@angular/animations';

@Component({
  selector: 'rms-mod',
  templateUrl: './rms-mod.component.html',
  styleUrls: ['./rms-mod.component.scss'],
  imports: [CommonModule, MatIconModule, MatButtonModule],
  animations: [
    trigger('morphSize', [
      state('list', style({ width: '{{collapsed}}', maxWidth: '{{collapsed}}' }), { params: { collapsed: '25%' } }),
      state('detail', style({ width: '{{expanded}}', maxWidth: '{{expanded}}' }), { params: { expanded: '98%' } }),
      transition('list <=> detail', [
        group([
          // ✅ Parent width animation
          animate('1s ease'),

          // ✅ Allow child fades to run AT THE SAME TIME
          query('@morphFrontFade, @morphBackFade', animateChild(), {
            optional: true
          })
        ]),
      ], { params: { growShrinkTime: '1s ease' } })
    ]),

    trigger('morphFrontFade', [
      state('list', style({ opacity: 1, pointerEvents: 'auto' })),
      state('detail', style({ opacity: 0, pointerEvents: 'none' })),

      transition('list => detail', [
        style({ opacity: 1 }),
        animate('.5s ease', style({ opacity: 0 }))
      ]),

      transition('detail => list', [
        style({ opacity: 0 }),
        animate('.5s ease', style({ opacity: 1 }))
      ]),
    ]),

    trigger('morphBackFade', [
      state('list', style({ opacity: 0, pointerEvents: 'none' })),
      state('detail', style({ opacity: 1, pointerEvents: 'auto' })),

      transition('list => detail', [
        style({ opacity: 0 }),
        animate('.5s ease', style({ opacity: 1 }))
      ]),

      transition('detail => list', [
        style({ opacity: 1 }),
        animate('.5s ease', style({ opacity: 0 }))
      ]),
    ]),
  ]
})
export class RmsModComponent {
  @Input() width: string = '';
  @Input() height: string = '100%';
  @Input() maxHeight: string = '';
  @Input() minHeight: string = '';
  @Input() backButton: boolean = false;
  @Input() flippable = false;
  @Input() flipped = false;
  @Input() includeHeader = false;

  @Input() transitionMode: 'flip' | 'morph' = 'flip';

  @Input() collapsedWidth: string = '25%';
  @Input() expandedWidth: string = '98%';

  @Input() growTime: string = '2ms cubic-bezier(.2,.9,.2,1)';
  @Input() shrinkTime: string = '2ms cubic-bezier(.4,0,.2,1)';
  @Input() inTime: string = '2ms ease';
  @Input() outTime: string = '2ms ease';

  @Output() backButtonClicked = new EventEmitter<void>();
  get isMorph() { return this.flippable && this.transitionMode === 'morph'; }
  get isFlip() { return this.flippable && this.transitionMode === 'flip'; }

  // one canonical state value for both triggers
  get viewState(): 'list' | 'detail' {
    return this.flipped ? 'detail' : 'list';
  }

  get morphState() {
    return this.viewState;
  }


  // Host size binding (only when morph mode)
  get morphSizeBinding() {
    if (!this.isMorph) return null;
    return {
      value: this.viewState,
      params: {
        collapsed: this.collapsedWidth,
        expanded: this.expandedWidth,
        growTime: this.growTime,
        shrinkTime: this.shrinkTime,
      }
    };
  }

  // Front face binding params
  get frontFaceBinding() {
    return {
      value: this.viewState,
      params: {
        inTime: this.inTime,
        outTime: this.outTime,
      }
    };
  }

  // Back face binding params (reverse)
  get backFaceBinding() {
    return {
      value: this.viewState,
      params: {
        inTime: this.inTime,
        outTime: this.outTime,
      }
    };
  }

  onBackButtonClick() {
    this.backButtonClicked.emit();
  }
}
